import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  fetchMeasureDiaryByDate,
  fetchMeasureDiaryHistory,
  fetchMeasureDiaryInfo,
  fetchMeasureDiarySeries,
  fetchLatestMeasurePhoto,
  fetchPreferenzeCompatibleProtocols,
  fetchPreferenzeGoalsList,
  PROTOCOL_START_SCHEDULED,
  savePreferenzeDiet,
  savePreferenzeProtocolTimeline,
  savePreferenzeSelectedGoals,
  upsertMeasureDiary,
  uploadAndSaveMeasureDiaryPhoto,
} from "../actions/mioPercorsoActions";

const emptyEntry = (date) => ({
  id: null,
  date,
  weight: null,
  neck_circumference: null,
  umbilical_circumference: null,
  hips_circumference: null,
  middle_thigh_circumference: null,
  arm_circumference: null,
  picture_front: null,
});

const initialState = {
  selectedDate: dayjs().format("YYYY-MM-DD"),

  info: null,
  infoLoading: false,
  infoError: null,

  entry: emptyEntry(dayjs().format("YYYY-MM-DD")),
  entryLoading: false,
  entryError: null,

  saveLoading: false,
  saveError: null,

  photoLoading: false,
  photoError: null,

  // Most recently uploaded photo across all dates. Used as a fallback so the
  // Misure photo box keeps showing the latest photo when the selected date has
  // none (persists across navigation + day changes).
  lastPhoto: { date: null, url: null },

  history: {
    anchorDate: dayjs().format("YYYY-MM-DD"),
    direction: "desc",
    pageSize: 10,
    page: 1,
    items: [],
    hasMore: true,
    loading: false,
    error: null,
  },

  // Full flat measurement history for the Progressi chart / Confronto table.
  series: {
    items: [],
    loading: false,
    error: null,
  },

  preferenze: {
    goals: {
      list: [],
      savedIds: [],
      draftIds: [],
      max: 5,
      loading: false,
      saving: false,
      error: null,
      dirty: false,
    },
    protocols: {
      list: [],
      loading: false,
      saving: false,
      error: null,
      activeProtocolId: null,
    },
    diet: {
      saved: {
        keep_weight: false,
        vegetarian: false,
        gluten_free: false,
        is_smart: false,
      },
      draft: {
        keep_weight: false,
        vegetarian: false,
        gluten_free: false,
        is_smart: false,
      },
      saving: false,
      error: null,
      dirty: false,
    },
  },
};

const mioPercorsoSlice = createSlice({
  name: "mioPercorso",
  initialState,
  reducers: {
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
      state.entry = emptyEntry(action.payload);
      state.entryError = null;
    },
    resetHistory: (state, action) => {
      const anchor = action.payload?.anchorDate || state.selectedDate;
      const direction = action.payload?.direction || state.history.direction;

      state.history.anchorDate = anchor;
      state.history.direction = direction;
      state.history.page = 1;
      state.history.items = [];
      state.history.hasMore = true;
      state.history.loading = false;
      state.history.error = null;
    },
    setHistoryDirection: (state, action) => {
      state.history.direction = action.payload;
      state.history.page = 1;
      state.history.items = [];
      state.history.hasMore = true;
      state.history.error = null;
    },

    // Preferenze - Goals
    setPreferenzeGoalsDraft: (state, action) => {
      const ids = Array.isArray(action.payload) ? action.payload : [];
      state.preferenze.goals.draftIds = ids;
      state.preferenze.goals.dirty =
        JSON.stringify([...ids].sort()) !==
        JSON.stringify([...(state.preferenze.goals.savedIds || [])].sort());
      state.preferenze.goals.error = null;
    },
    resetPreferenzeGoalsDraft: (state) => {
      state.preferenze.goals.draftIds = [];
      state.preferenze.goals.dirty =
        (state.preferenze.goals.savedIds || []).length !== 0;
      state.preferenze.goals.error = null;
    },
    cancelPreferenzeGoalsChanges: (state) => {
      state.preferenze.goals.draftIds = state.preferenze.goals.savedIds || [];
      state.preferenze.goals.dirty = false;
      state.preferenze.goals.error = null;
    },

    // Preferenze - Diet
    hydratePreferenzeDietFromUser: (state, action) => {
      const p = action.payload || {};
      const saved = {
        keep_weight: !!p.keep_weight,
        vegetarian: typeof p.vegetarian === "boolean" ? p.vegetarian : null,
        gluten_free: !!p.gluten_free,
        is_smart: !!p.is_smart,
      };
      state.preferenze.diet.saved = saved;
      if (!state.preferenze.diet.dirty) {
        state.preferenze.diet.draft = { ...saved };
      }
    },
    setPreferenzeDietDraft: (state, action) => {
      const next = {
        ...(state.preferenze.diet.draft || {}),
        ...(action.payload || {}),
      };
      state.preferenze.diet.draft = next;
      state.preferenze.diet.dirty =
        JSON.stringify(next) !== JSON.stringify(state.preferenze.diet.saved);
      state.preferenze.diet.error = null;
    },
    cancelPreferenzeDietChanges: (state) => {
      state.preferenze.diet.draft = { ...(state.preferenze.diet.saved || {}) };
      state.preferenze.diet.dirty = false;
      state.preferenze.diet.error = null;
    },
  },
  extraReducers: (builder) => {
    // Info
    builder.addCase(fetchMeasureDiaryInfo.pending, (state) => {
      state.infoLoading = true;
      state.infoError = null;
    });
    builder.addCase(fetchMeasureDiaryInfo.fulfilled, (state, action) => {
      state.infoLoading = false;
      state.info = action.payload;
    });
    builder.addCase(fetchMeasureDiaryInfo.rejected, (state, action) => {
      state.infoLoading = false;
      state.infoError = action.payload || action.error?.message;
    });

    // Full measurement series (Progressi chart / Confronto table)
    builder.addCase(fetchMeasureDiarySeries.pending, (state) => {
      state.series.loading = true;
      state.series.error = null;
    });
    builder.addCase(fetchMeasureDiarySeries.fulfilled, (state, action) => {
      state.series.loading = false;
      state.series.items = Array.isArray(action.payload) ? action.payload : [];
    });
    builder.addCase(fetchMeasureDiarySeries.rejected, (state, action) => {
      state.series.loading = false;
      state.series.error = action.payload || action.error?.message;
    });

    // Latest uploaded photo (fallback for the Misure photo box)
    builder.addCase(fetchLatestMeasurePhoto.fulfilled, (state, action) => {
      state.lastPhoto = action.payload?.url
        ? { date: action.payload.date, url: action.payload.url }
        : { date: null, url: null };
    });

    // Fetch by date
    builder.addCase(fetchMeasureDiaryByDate.pending, (state) => {
      state.entryLoading = true;
      state.entryError = null;
    });
    builder.addCase(fetchMeasureDiaryByDate.fulfilled, (state, action) => {
      state.entryLoading = false;
      const date = action.payload?.date || state.selectedDate;
      const data = action.payload?.data || {};
      state.entry = { ...emptyEntry(date), ...data, date };
      state.selectedDate = date;

      // Track the latest known photo so the box can fall back to it on dates
      // that have none. Only bump when this date is at least as recent.
      if (
        data?.picture_front &&
        (!state.lastPhoto?.date || String(date) >= String(state.lastPhoto.date))
      ) {
        state.lastPhoto = { date, url: data.picture_front };
      }
    });
    builder.addCase(fetchMeasureDiaryByDate.rejected, (state, action) => {
      state.entryLoading = false;
      state.entryError = action.payload || action.error?.message;
      state.entry = emptyEntry(state.selectedDate);
    });

    // Upsert
    builder.addCase(upsertMeasureDiary.pending, (state) => {
      state.saveLoading = true;
      state.saveError = null;
    });
    builder.addCase(upsertMeasureDiary.fulfilled, (state, action) => {
      state.saveLoading = false;

      const date = action.payload?.date || state.selectedDate;
      const patch = action.payload?.patchApplied || {};
      const data = action.payload?.data || {};

      state.entry = {
        ...state.entry,
        ...(data?.id ? data : {}),
        ...patch,
        date,
      };
    });
    builder.addCase(upsertMeasureDiary.rejected, (state, action) => {
      state.saveLoading = false;
      state.saveError = action.payload || action.error?.message;
    });

    // Upload + Save photo
    builder.addCase(uploadAndSaveMeasureDiaryPhoto.pending, (state) => {
      state.photoLoading = true;
      state.photoError = null;
    });
    builder.addCase(
      uploadAndSaveMeasureDiaryPhoto.fulfilled,
      (state, action) => {
        state.photoLoading = false;
        const date = action.payload?.date || state.selectedDate;
        const picture_front = action.payload?.picture_front ?? null;
        state.entry = { ...state.entry, date, picture_front };
      },
    );
    builder.addCase(
      uploadAndSaveMeasureDiaryPhoto.rejected,
      (state, action) => {
        state.photoLoading = false;
        state.photoError = action.payload || action.error?.message;
      },
    );

    // History
    builder.addCase(fetchMeasureDiaryHistory.pending, (state, action) => {
      if (!action.meta.arg?.append) state.history.loading = true;
      state.history.error = null;
    });
    builder.addCase(fetchMeasureDiaryHistory.fulfilled, (state, action) => {
      state.history.loading = false;

      const { items, append, page, hasMore, date, direction, pageSize } =
        action.payload;

      state.history.anchorDate = date;
      state.history.direction = direction;
      state.history.pageSize = pageSize;
      state.history.page = page;
      state.history.hasMore = hasMore;

      if (!append || page === 1) {
        state.history.items = items || [];
      } else {
        state.history.items = [
          ...(state.history.items || []),
          ...(items || []),
        ];
      }
    });
    builder.addCase(fetchMeasureDiaryHistory.rejected, (state, action) => {
      state.history.loading = false;
      state.history.error = action.payload || action.error?.message;
    });

    /** -------- Preferenze -------- */
    builder.addCase(fetchPreferenzeGoalsList.pending, (state) => {
      state.preferenze.goals.loading = true;
      state.preferenze.goals.error = null;
    });
    builder.addCase(fetchPreferenzeGoalsList.fulfilled, (state, action) => {
      state.preferenze.goals.loading = false;
      state.preferenze.goals.list = action.payload?.goals || [];
      const selected = action.payload?.selectedIds || [];
      state.preferenze.goals.savedIds = selected;
      if (!state.preferenze.goals.dirty) {
        state.preferenze.goals.draftIds = selected;
      }
      state.preferenze.goals.dirty =
        JSON.stringify([...(state.preferenze.goals.draftIds || [])].sort()) !==
        JSON.stringify([...(state.preferenze.goals.savedIds || [])].sort());
    });
    builder.addCase(fetchPreferenzeGoalsList.rejected, (state, action) => {
      state.preferenze.goals.loading = false;
      state.preferenze.goals.error = action.payload || action.error?.message;
    });

    builder.addCase(savePreferenzeSelectedGoals.pending, (state) => {
      state.preferenze.goals.saving = true;
      state.preferenze.goals.error = null;
    });
    builder.addCase(savePreferenzeSelectedGoals.fulfilled, (state, action) => {
      state.preferenze.goals.saving = false;
      const saved = action.payload?.savedIds || [];
      state.preferenze.goals.savedIds = saved;
      state.preferenze.goals.draftIds = saved;
      state.preferenze.goals.dirty = false;
    });
    builder.addCase(savePreferenzeSelectedGoals.rejected, (state, action) => {
      state.preferenze.goals.saving = false;
      state.preferenze.goals.error = action.payload || action.error?.message;
    });

    builder.addCase(fetchPreferenzeCompatibleProtocols.pending, (state) => {
      state.preferenze.protocols.loading = true;
      state.preferenze.protocols.error = null;
    });
    builder.addCase(
      fetchPreferenzeCompatibleProtocols.fulfilled,
      (state, action) => {
        state.preferenze.protocols.loading = false;
        state.preferenze.protocols.list = action.payload?.protocols || [];
        if (
          action.payload &&
          Object.prototype.hasOwnProperty.call(
            action.payload,
            "activeProtocolId",
          )
        ) {
          state.preferenze.protocols.activeProtocolId =
            action.payload.activeProtocolId;
        }
      },
    );
    builder.addCase(
      fetchPreferenzeCompatibleProtocols.rejected,
      (state, action) => {
        state.preferenze.protocols.loading = false;
        // A background refetch (fired right after activating/deactivating a
        // percorso) can fail transiently. If we already have protocols on
        // screen, keep the current list and stay silent — the selected percorso
        // still works. Surface the error only on a true first load (nothing to
        // fall back to).
        const hasList =
          Array.isArray(state.preferenze.protocols.list) &&
          state.preferenze.protocols.list.length > 0;
        state.preferenze.protocols.error = hasList
          ? null
          : action.payload || action.error?.message;
      },
    );

    builder.addCase(savePreferenzeProtocolTimeline.pending, (state) => {
      state.preferenze.protocols.saving = true;
      state.preferenze.protocols.error = null;
    });
    builder.addCase(
      savePreferenzeProtocolTimeline.fulfilled,
      (state, action) => {
        state.preferenze.protocols.saving = false;
        // activeProtocolId will be refreshed from user profile; but we optimistically set it.
        // Not for a booking that starts weeks later: today's percorso is unchanged.
        if (
          action.payload?.protocol_id != null &&
          action.payload?.start_type !== PROTOCOL_START_SCHEDULED
        ) {
          state.preferenze.protocols.activeProtocolId =
            action.payload.protocol_id;
        }
      },
    );
    builder.addCase(
      savePreferenzeProtocolTimeline.rejected,
      (state, action) => {
        state.preferenze.protocols.saving = false;
        state.preferenze.protocols.error =
          action.payload || action.error?.message;
      },
    );

    builder.addCase(savePreferenzeDiet.pending, (state) => {
      state.preferenze.diet.saving = true;
      state.preferenze.diet.error = null;
    });
    builder.addCase(savePreferenzeDiet.fulfilled, (state, action) => {
      state.preferenze.diet.saving = false;
      const saved = action.payload || state.preferenze.diet.draft;
      state.preferenze.diet.saved = { ...saved };
      state.preferenze.diet.draft = { ...saved };
      state.preferenze.diet.dirty = false;
    });
    builder.addCase(savePreferenzeDiet.rejected, (state, action) => {
      state.preferenze.diet.saving = false;
      state.preferenze.diet.error = action.payload || action.error?.message;
    });
  },
});

export const {
  setSelectedDate,
  resetHistory,
  setHistoryDirection,
  setPreferenzeGoalsDraft,
  resetPreferenzeGoalsDraft,
  cancelPreferenzeGoalsChanges,
  hydratePreferenzeDietFromUser,
  setPreferenzeDietDraft,
  cancelPreferenzeDietChanges,
} = mioPercorsoSlice.actions;
export default mioPercorsoSlice.reducer;
